import {
  DeleteOutlined,
  EditOutlined,
  PlusOutlined,
  UserOutlined,
} from "@ant-design/icons";
import {
  Button,
  Drawer,
  Empty,
  Form,
  Input,
  List,
  Modal,
  Popconfirm,
  Spin,
  Typography,
} from "antd";
import { useState } from "react";
import { IExternalEmployee } from "../../models/Timesheet";
import {
  useCreateExternalEmployee,
  useDeleteExternalEmployee,
  useGetExternalEmployee,
  useUpdateExternalEmployee,
} from "../../query/TimesheetQuery";
import { notify } from "../../utils/toast";

const { Text } = Typography;

interface DrawerPartTimeEmployeesProps {
  open: boolean;
  onClose: () => void;
}

const DrawerPartTimeEmployees = ({
  open,
  onClose,
}: DrawerPartTimeEmployeesProps) => {
  const [form] = Form.useForm();
  const [editingEmployee, setEditingEmployee] =
    useState<IExternalEmployee | null>(null);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);

  const { data: employees, isLoading } = useGetExternalEmployee();
  const createEmployee = useCreateExternalEmployee();
  const updateEmployee = useUpdateExternalEmployee();
  const deleteEmployee = useDeleteExternalEmployee();

  const handleAddEmployee = () => {
    form.resetFields();
    setShowAddModal(true);
  };

  const handleEditEmployee = (employee: IExternalEmployee) => {
    setEditingEmployee(employee);
    form.setFieldsValue({
      fullName: employee.fullName,
      email: employee.email,
      phone: employee.phone,
      position: employee.position,
      department: employee.department,
      note: employee.note,
    });
    setShowEditModal(true);
  };

  const handleAddSubmit = async () => {
    try {
      const values = await form.validateFields();
      await createEmployee.mutateAsync(values);
      notify.success("Thêm nhân viên thành công");
      setShowAddModal(false);
      form.resetFields();
    } catch (error) {
      notify.error("Thêm nhân viên thất bại");
    }
  };

  const handleEditSubmit = async () => {
    try {
      const values = await form.validateFields();
      if (!editingEmployee) return;

      await updateEmployee.mutateAsync({
        id: editingEmployee.id,
        data: values,
      });
      notify.success("Cập nhật nhân viên thành công");
      setShowEditModal(false);
      setEditingEmployee(null);
      form.resetFields();
    } catch (error) {
      notify.error("Cập nhật nhân viên thất bại");
    }
  };

  const handleDeleteEmployee = async (id: number) => {
    try {
      await deleteEmployee.mutateAsync(id);
      notify.success("Xóa nhân viên thành công");
    } catch (error) {
      notify.error("Xóa nhân viên thất bại");
    }
  };

  const handleModalCancel = () => {
    setShowAddModal(false);
    setShowEditModal(false);
    setEditingEmployee(null);
    form.resetFields();
  };

  return (
    <>
      <Drawer
        title="Danh sách nhân viên Part Time"
        placement="right"
        onClose={onClose}
        open={open}
        width={600}
        extra={
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={handleAddEmployee}
          >
            Thêm nhân viên
          </Button>
        }
      >
        {isLoading ? (
          <div className="flex justify-center items-center h-64">
            <Spin size="large" />
          </div>
        ) : employees && employees.length > 0 ? (
          <List
            dataSource={employees}
            renderItem={(employee) => (
              <List.Item
                actions={[
                  <Button
                    type="text"
                    icon={<EditOutlined />}
                    onClick={() => handleEditEmployee(employee)}
                  >
                    Sửa
                  </Button>,
                  <Popconfirm
                    title="Xóa nhân viên"
                    description="Bạn có chắc chắn muốn xóa nhân viên này?"
                    onConfirm={() => handleDeleteEmployee(employee.id)}
                    okText="Xóa"
                    cancelText="Hủy"
                    okButtonProps={{ danger: true }}
                  >
                    <Button type="text" danger icon={<DeleteOutlined />}>
                      Xóa
                    </Button>
                  </Popconfirm>,
                ]}
              >
                <List.Item.Meta
                  avatar={<UserOutlined className="text-2xl text-gray-400" />}
                  title={
                    <div className="flex items-center gap-2">
                      <Text strong>{employee.fullName}</Text>
                      <Text type="secondary">({employee.position})</Text>
                    </div>
                  }
                  description={
                    <div className="space-y-1">
                      <div>
                        <Text type="secondary">Email: </Text>
                        <Text>{employee.email}</Text>
                      </div>
                      <div>
                        <Text type="secondary">SĐT: </Text>
                        <Text>{employee.phone}</Text>
                      </div>
                      {employee.department && (
                        <div>
                          <Text type="secondary">Phòng ban: </Text>
                          <Text>{employee.department}</Text>
                        </div>
                      )}
                      {employee.note && (
                        <div>
                          <Text type="secondary">Ghi chú: </Text>
                          <Text>{employee.note}</Text>
                        </div>
                      )}
                    </div>
                  }
                />
              </List.Item>
            )}
          />
        ) : (
          <Empty
            description="Chưa có nhân viên part time nào"
            image={Empty.PRESENTED_IMAGE_SIMPLE}
          />
        )}
      </Drawer>

      <Modal
        title="Thêm nhân viên Part Time"
        open={showAddModal}
        onOk={handleAddSubmit}
        onCancel={handleModalCancel}
        okText="Thêm"
        cancelText="Hủy"
        width={500}
        confirmLoading={createEmployee.isPending}
      >
        <Form form={form} layout="vertical" className="mt-4">
          <Form.Item
            label="Họ và tên"
            name="fullName"
            rules={[{ required: true, message: "Vui lòng nhập họ và tên" }]}
          >
            <Input placeholder="Nhập họ và tên" />
          </Form.Item>

          <Form.Item
            label="Email"
            name="email"
            rules={[
              { required: true, message: "Vui lòng nhập email" },
              { type: "email", message: "Email không hợp lệ" },
            ]}
          >
            <Input placeholder="Nhập email" />
          </Form.Item>

          <Form.Item
            label="Số điện thoại"
            name="phone"
            rules={[{ required: true, message: "Vui lòng nhập số điện thoại" }]}
          >
            <Input placeholder="Nhập số điện thoại" />
          </Form.Item>

          <Form.Item
            label="Vị trí"
            name="position"
            rules={[{ required: true, message: "Vui lòng nhập vị trí" }]}
          >
            <Input placeholder="Nhập vị trí công việc" />
          </Form.Item>

          <Form.Item label="Phòng ban" name="department">
            <Input placeholder="Nhập phòng ban" />
          </Form.Item>

          <Form.Item label="Ghi chú" name="note">
            <Input.TextArea placeholder="Nhập ghi chú" rows={3} />
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        title="Chỉnh sửa nhân viên Part Time"
        open={showEditModal}
        onOk={handleEditSubmit}
        onCancel={handleModalCancel}
        okText="Cập nhật"
        cancelText="Hủy"
        width={500}
        confirmLoading={updateEmployee.isPending}
      >
        <Form form={form} layout="vertical" className="mt-4">
          <Form.Item
            label="Họ và tên"
            name="fullName"
            rules={[{ required: true, message: "Vui lòng nhập họ và tên" }]}
          >
            <Input placeholder="Nhập họ và tên" />
          </Form.Item>

          <Form.Item
            label="Email"
            name="email"
            rules={[
              { required: true, message: "Vui lòng nhập email" },
              { type: "email", message: "Email không hợp lệ" },
            ]}
          >
            <Input placeholder="Nhập email" />
          </Form.Item>

          <Form.Item
            label="Số điện thoại"
            name="phone"
            rules={[{ required: true, message: "Vui lòng nhập số điện thoại" }]}
          >
            <Input placeholder="Nhập số điện thoại" />
          </Form.Item>

          <Form.Item
            label="Vị trí"
            name="position"
            rules={[{ required: true, message: "Vui lòng nhập vị trí" }]}
          >
            <Input placeholder="Nhập vị trí công việc" />
          </Form.Item>

          <Form.Item label="Phòng ban" name="department">
            <Input placeholder="Nhập phòng ban" />
          </Form.Item>

          <Form.Item label="Ghi chú" name="note">
            <Input.TextArea placeholder="Nhập ghi chú" rows={3} />
          </Form.Item>
        </Form>
      </Modal>
    </>
  );
};

export default DrawerPartTimeEmployees;
